<template>
  <div class="prostate-report-template" data-testid="prostate-report-template">
    <section class="report-section">
      <h3>{{ $t('clinical-description-section') }}</h3>
      <div class="field">
        <label class="label">{{ $t('clinical-description') }}</label>
        <textarea class="textarea" rows="3" v-model="report.clinical.description" :maxlength="10000"></textarea>
      </div>
      <div class="structured-grid prostate-clinical-grid">
        <div class="field">
          <label class="label">{{ $t('dre') }}</label>
          <input class="input" type="text" v-model="report.clinical.dre">
        </div>
        <div class="field">
          <label class="label">{{ $t('psa') }}</label>
          <input class="input" type="text" v-model="report.clinical.psa">
        </div>
        <div class="field">
          <label class="label">{{ $t('trus') }}</label>
          <input class="input" type="text" v-model="report.clinical.trus">
        </div>
      </div>
    </section>

    <section class="report-section">
      <div class="field-heading">
        <h3>{{ $t('type-of-specimen-section') }}</h3>
        <button class="button is-small add-measurement-button" type="button" @click="addCustomSpecimen">
          {{ $t('prostate-add-specimen') }}
        </button>
      </div>
      <div class="checkbox-grid prostate-site-grid" data-testid="prostate-specimen-list">
        <div v-for="(specimen, index) in report.prostate.specimens" :key="specimen.id" class="prostate-site-choice">
          <label class="checkbox report-checkbox">
            <input type="checkbox" v-model="specimen.selected" @change="handleSpecimenSelectionChange">
            <span>
              <strong>{{ specimen.code }}.</strong>
              {{ specimenLabel(specimen) }}
            </span>
          </label>
          <div v-if="isCustomSpecimen(specimen)" class="prostate-custom-site-row">
            <input class="input" type="text" v-model="specimen.customLabel" :placeholder="$t('custom-specimen-site-placeholder')">
            <button
              class="button is-small remove-measurement-button"
              type="button"
              @click="removeSpecimen(index)"
            >
              {{ $t('button-remove') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="report-section" data-testid="prostate-macroscopic-section">
      <h3>{{ $t('macroscopic-finding-section') }}</h3>
      <p v-if="!selectedSpecimens.length" class="help">{{ $t('prostate-no-specimen-selected') }}</p>
      <div v-else class="prostate-section-rows">
        <div v-for="specimen in selectedSpecimens" :key="`macro-${specimen.id}`" class="prostate-section-row" data-testid="prostate-macroscopic-row">
          <div class="prostate-row-title">
            <span class="specimen-code">{{ specimen.code }}</span>
            <span>{{ specimenLabel(specimen) }}</span>
          </div>
          <div class="structured-grid prostate-macro-grid">
            <div class="field">
              <label class="small-label">{{ $t('prostate-core-count') }}</label>
              <input class="input" type="number" min="0" step="1" v-model="specimen.coreCount">
            </div>
            <div class="field">
              <label class="small-label">{{ $t('prostate-core-lengths-mm') }}</label>
              <input class="input" type="text" v-model="specimen.coreLengths" :placeholder="$t('prostate-core-lengths-placeholder')">
            </div>
            <div class="field">
              <label class="small-label">{{ $t('prostate-block-count') }}</label>
              <input class="input" type="number" min="0" step="1" v-model="specimen.block">
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="report-section" data-testid="prostate-microscopic-section">
      <h3>{{ $t('microscopic-finding-section') }}</h3>
      <p v-if="!selectedSpecimens.length" class="help">{{ $t('prostate-no-specimen-selected') }}</p>
      <div v-else class="prostate-section-rows">
        <div v-for="specimen in selectedSpecimens" :key="`micro-${specimen.id}`" class="prostate-section-row" data-testid="prostate-microscopic-row">
          <div class="prostate-row-title">
            <span class="specimen-code">{{ specimen.code }}</span>
            <span>{{ specimenLabel(specimen) }}</span>
          </div>
          <div class="field">
            <label class="small-label">{{ $t('microscopic-finding-section') }}</label>
            <div class="select is-fullwidth">
              <select
                v-model="specimen.result"
                data-testid="prostate-specimen-result"
                @change="handleSpecimenResultChange(specimen)"
              >
                <option v-for="option in specimenResultOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="specimen.result === 'other'" class="field">
            <label class="small-label">{{ $t('other-specify') }}</label>
            <textarea class="textarea" rows="2" v-model="specimen.resultOther" :maxlength="5000"></textarea>
          </div>

          <div v-if="specimen.result === 'acinar-adenocarcinoma'" class="prostate-cancer-fields" data-testid="prostate-cancer-fields">
            <div class="structured-grid prostate-cancer-grid">
              <div class="field">
                <label class="small-label">{{ $t('positive-cores') }}</label>
                <input
                  class="input"
                  type="number"
                  min="0"
                  :max="positiveCoreMax(specimen)"
                  step="1"
                  v-model="specimen.positiveCores"
                  data-testid="positive-cores-input"
                  @input="clampPositiveCores(specimen)"
                >
              </div>
              <div class="field">
                <label class="small-label">{{ $t('total-cores') }}</label>
                <input class="input" type="text" :value="displayCoreCount(specimen)" disabled>
              </div>
              <div class="field">
                <label class="small-label">{{ $t('tumour-volume') }}</label>
                <input class="input" type="text" v-model="specimen.tumourVolume" :placeholder="$t('tumour-volume-placeholder')">
              </div>
              <div class="field">
                <label class="small-label">{{ $t('gleason-score') }}</label>
                <div class="select is-fullwidth">
                  <select
                    v-model="specimen.gleasonScore"
                    data-testid="gleason-score-select"
                    @change="syncGradeGroup(specimen)"
                  >
                    <option v-for="option in gleasonScoreOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="field">
                <label class="small-label">{{ $t('grade-group') }}</label>
                <input
                  class="input"
                  type="text"
                  :value="displayGradeGroup(specimen)"
                  disabled
                  data-testid="grade-group-value"
                >
              </div>
              <div v-if="hasPatternFour(specimen)" class="field">
                <label class="small-label">{{ $t('cribriform') }}</label>
                <div class="select is-fullwidth">
                  <select v-model="specimen.cribriform">
                    <option v-for="option in presenceOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="report-section">
      <h3>{{ $t('other-finding-section') }}</h3>
      <div class="structured-grid prostate-other-grid">
        <div class="field">
          <label class="label">{{ $t('high-grade-pin') }}</label>
          <div class="select is-fullwidth">
            <select v-model="report.prostate.highGradePin" @change="clearFindingDetail('highGradePin')">
              <option v-for="option in findingStatusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div v-if="report.prostate.highGradePin === 'present'" class="field">
          <label class="label">{{ $t('high-grade-pin-detail') }}</label>
          <input class="input" type="text" v-model="report.prostate.highGradePinDetail">
        </div>
        <div class="field">
          <label class="label">{{ $t('idc-p') }}</label>
          <div class="select is-fullwidth">
            <select v-model="report.prostate.idcp" @change="clearFindingDetail('idcp')">
              <option v-for="option in findingStatusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div v-if="report.prostate.idcp === 'present'" class="field">
          <label class="label">{{ $t('idc-p-detail') }}</label>
          <input class="input" type="text" v-model="report.prostate.idcpDetail">
        </div>
      </div>
      <div class="field">
        <label class="label">{{ $t('inflammation') }}</label>
        <textarea class="textarea" rows="2" v-model="report.prostate.inflammation" :maxlength="5000"></textarea>
      </div>
      <div v-if="hasCancer" class="structured-grid prostate-other-grid" data-testid="prostate-cancer-only-findings">
        <div v-for="field in cancerFindingFields" :key="field.key" class="field">
          <label class="label">{{ field.label }}</label>
          <div class="select is-fullwidth">
            <select v-model="report.prostate[field.key]">
              <option v-for="option in invasionStatusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </section>

    <section class="report-section">
      <div class="field-heading">
        <h3>{{ $t('final-interpretation-section') }}</h3>
        <button
          class="button is-small"
          type="button"
          :disabled="!suggestedFinalInterpretation"
          data-testid="generate-final-interpretation-button"
          @click="applySuggestedFinalInterpretation"
        >
          {{ $t('button-generate-final-interpretation') }}
        </button>
      </div>
      <div class="field">
        <label class="label">{{ $t('final-interpretation') }}</label>
        <textarea
          class="textarea"
          rows="6"
          v-model="report.finalInterpretation"
          :maxlength="15000"
          :placeholder="$t('prostate-final-interpretation-placeholder')"
          data-testid="final-interpretation"
        ></textarea>
      </div>
    </section>
  </div>
</template>

<script>
import { optionPairs, translateOptions } from './reportTemplateOptions';
import {
  buildProstateFinalInterpretation,
  createProstateSpecimen,
  gradeGroupForGleason,
  hasProstateCancer,
  getSelectedProstateSpecimens
} from './reportTemplateText';

export default {
  name: 'ProstateReportTemplate',
  props: {
    report: {
      type: Object,
      required: true
    }
  },
  computed: {
    specimenResultOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.prostateSpecimenResult);
    },
    gleasonScoreOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.gleasonScore);
    },
    presenceOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.presence);
    },
    findingStatusOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.prostateFindingStatus);
    },
    invasionStatusOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.prostateInvasionStatus);
    },
    cancerFindingFields() {
      return [
        { key: 'periprostaticFatInvasion', label: this.$t('periprostatic-fat-invasion') },
        { key: 'seminalVesicleInvasion', label: this.$t('seminal-vesicle-invasion') },
        { key: 'lymphovascularInvasion', label: this.$t('lymphovascular-invasion') },
        { key: 'perineuralInvasion', label: this.$t('perineural-invasion') }
      ];
    },
    selectedSpecimens() {
      return getSelectedProstateSpecimens(this.report);
    },
    hasCancer() {
      return hasProstateCancer(this.report);
    },
    suggestedFinalInterpretation() {
      return buildProstateFinalInterpretation(this.report, this.$t.bind(this));
    }
  },
  methods: {
    specimenLabel(specimen) {
      return specimen.customLabel || (specimen.labelKey ? this.$t(specimen.labelKey) : this.$t('option-other'));
    },
    isCustomSpecimen(specimen) {
      return specimen.id && specimen.id.startsWith('custom-');
    },
    addCustomSpecimen() {
      const nextCode = String.fromCharCode(65 + this.report.prostate.specimens.length);
      this.report.prostate.specimens.push(createProstateSpecimen({
        id: `custom-${this.report.prostate.specimens.length + 1}`,
        code: nextCode,
        customLabel: '',
        selected: true
      }));
    },
    removeSpecimen(index) {
      this.report.prostate.specimens.splice(index, 1);
      this.report.prostate.specimens.forEach((specimen, specimenIndex) => {
        specimen.code = String.fromCharCode(65 + specimenIndex);
      });
      this.handleSpecimenSelectionChange();
    },
    displayCoreCount(specimen) {
      return specimen.coreCount || this.$t('not-available');
    },
    positiveCoreMax(specimen) {
      return specimen.coreCount === '' ? undefined : specimen.coreCount;
    },
    clampPositiveCores(specimen) {
      if (specimen.coreCount !== '' && Number(specimen.positiveCores) > Number(specimen.coreCount)) {
        specimen.positiveCores = specimen.coreCount;
      }
    },
    syncGradeGroup(specimen) {
      specimen.gradeGroup = gradeGroupForGleason(specimen.gleasonScore);
      if (!this.hasPatternFour(specimen)) {
        specimen.cribriform = '';
      }
    },
    displayGradeGroup(specimen) {
      return specimen.gradeGroup ? this.$t(specimen.gradeGroup) : this.$t('not-available');
    },
    hasPatternFour(specimen) {
      return specimen.gleasonScore.split('+').includes('4');
    },
    handleSpecimenResultChange(specimen) {
      if (specimen.result !== 'other') {
        specimen.resultOther = '';
      }
      if (specimen.result !== 'acinar-adenocarcinoma') {
        specimen.positiveCores = '';
        specimen.tumourVolume = '';
        specimen.gleasonScore = '';
        specimen.gradeGroup = '';
        specimen.cribriform = '';
      }
      if (!this.hasCancer) {
        this.clearCancerFindings();
      }
    },
    handleSpecimenSelectionChange() {
      if (!this.hasCancer) {
        this.clearCancerFindings();
      }
    },
    clearCancerFindings() {
      this.cancerFindingFields.forEach(field => {
        this.report.prostate[field.key] = '';
      });
    },
    clearFindingDetail(key) {
      if (this.report.prostate[key] !== 'present') {
        this.report.prostate[`${key}Detail`] = '';
      }
    },
    applySuggestedFinalInterpretation() {
      this.report.finalInterpretation = this.suggestedFinalInterpretation;
    }
  }
};
</script>
